/**
 * Block 7: Unused and Released Time Section (Left Chart)
 * 
 * Bar chart showing unused_minutes and cancelled_minutes by month
 * Data source: meeting_room_mart_unused_cancelled
 */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { QUERY_STRING_STORE } from '@/helpers/constants';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import LoadingSpinner from '@/components/CustomComponent/LoadingSpinner';
import {
  fetchMeetingRoomMartUnusedCancelled,
  getLastNMonths,
  formatMonthShort,
} from './query-request';
import './UnusedReleasedTimeChart.less';

@connect(state => ({
  querystring: state.querystring,
}))
class UnusedReleasedTimeChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      chartData: [],
      months: [],
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  componentDidUpdate(prevProps) {
    const prevBranch = prevProps?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_BRANCH]?.[0];
    const currentBranch = this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_BRANCH]?.[0];
    if (prevBranch !== currentBranch) {
      this.fetchData();
    }
  }

  fetchData = async () => {
    const tenantId = this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0];
    const branchId = this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_BRANCH]?.[0];

    if (!tenantId) {
      this.setState({ chartData: [] });
      return;
    }

    this.setState({ isLoading: true });

    try {
      const months = getLastNMonths(4);
      const data = await fetchMeetingRoomMartUnusedCancelled({ tenantId, branchId, months });

      if (!data || data.length === 0) {
        this.setState({ chartData: [], isLoading: false });
        return;
      }

      const currentMonth = months[0];

      // Transform for chart
      const chartData = data
        .sort((a, b) => a.month.localeCompare(b.month))
        .map(d => ({
          month: formatMonthShort(d.month),
          monthFull: d.month,
          unused: d.unused_minutes || 0,
          cancelled: d.cancelled_minutes || 0,
          isCurrent: d.month === currentMonth,
        }));

      this.setState({
        chartData,
        months,
        isLoading: false,
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      this.setState({ chartData: [], isLoading: false });
    }
  };

  // Custom tooltip for left chart
  renderTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;

    const data = payload[0]?.payload;
    if (!data) return null;

    const [year, month] = (data.monthFull || '').split('-');

    return (
      <div className="custom-tooltip">
        <p className="tooltip-title">{year}年{parseInt(month)}月</p>
        <div className="tooltip-row">
          <span className="tooltip-label">空予約:</span>
          <span className="tooltip-value">{data.unused}分</span>
        </div>
        <div className="tooltip-row">
          <span className="tooltip-label">開放:</span>
          <span className="tooltip-value">{data.cancelled}分</span>
        </div>
      </div>
    );
  };

  renderChart = () => {
    const { chartData } = this.state;

    if (chartData.length === 0) {
      return <div className="no-data-container">データがありません</div>;
    }

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} margin={{ top: 30, right: 30, left: 20, bottom: 5 }} barGap={0}>
          <CartesianGrid strokeOpacity={1} stroke="#a2a8bb" strokeWidth={1} horizontal={true} vertical={true} />
          <XAxis dataKey="month" stroke="#a2a8bb" axisLine={{ stroke: '#a2a8bb' }} tickLine={{ stroke: '#a2a8bb' }} />
          <YAxis
            stroke="#a2a8bb"
            axisLine={{ stroke: '#a2a8bb' }}
            tickLine={{ stroke: '#a2a8bb' }}
            tickFormatter={(value) => value === 0 ? '(分)' : value}
          />
          <Tooltip content={this.renderTooltip} cursor={false} />
          <Bar dataKey="unused" name="空予約" stroke="#1c2340" strokeWidth={1}>
            {chartData.map((entry, index) => (
              <Cell
                key={`unused-${index}`}
                fill={entry.isCurrent ? '#d4a5c9' : '#e8d4e4'}
              />
            ))}
          </Bar>
          <Bar dataKey="cancelled" name="開放" stroke="#1c2340" strokeWidth={1}>
            {chartData.map((entry, index) => (
              <Cell
                key={`cancelled-${index}`}
                fill={entry.isCurrent ? '#f4a261' : '#fcd5b5'}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  };

  render() {
    const { isLoading } = this.state;

    return (
      <div className="unused-released-time-chart">
        <div className="section-header">
          <h3 className="section-title">空予約・開放時間</h3>
        </div>

        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="chart-content">
            {this.renderChart()}
          </div>
        )}
      </div>
    );
  }
}

export default UnusedReleasedTimeChart;
